/**
 * Mavrk Uniswap V3 Launch Math
 * 
 * Computes Uniswap V3 pool parameters for token launches with user-configurable pricing.
 * Based on Uniswap v3-core and v3-periphery math libraries.
 * 
 * CRITICAL: Computes BOTH token0 and token1 scenarios since token address ordering
 * cannot be determined until deployment time.
 * 
 * References:
 * - TickMath: https://github.com/Uniswap/v3-core/blob/main/contracts/libraries/TickMath.sol
 * - LiquidityAmounts: https://github.com/Uniswap/v3-periphery/blob/main/contracts/libraries/LiquidityAmounts.sol
 */

// Constants
export const TOTAL_SUPPLY = 1_000_000_000; // 1 billion tokens (fixed)
export const FEE_TIER = 10000; // 1% fee tier (10000 basis points)
export const TICK_SPACING = 200; // Tick spacing for 1% fee tier on Uniswap V3
export const WETH_ADDRESS = '0x4200000000000000000000000000000000000006'; // Ink WETH

// Uniswap V3 constants
const Q96 = BigInt(2) ** BigInt(96); // 2^96 for fixed-point math
const MIN_TICK = -887272;
const MAX_TICK = 887272;
const MIN_SQRT_RATIO = BigInt('4295128739');
const MAX_SQRT_RATIO = BigInt('1461446703485210103287273052203988822378723970342');

export interface LaunchParams {
    // Scenario 1: When new token is token0 (token < WETH)
    paramsToken0: {
        sqrtPriceX96: string;
        tickLower: number;
        tickUpper: number;
        amount0Desired: string;
        amount1Desired: string;
        amount0Min: string;
        amount1Min: string;
    };

    // Scenario 2: When new token is token1 (WETH < token)
    paramsToken1: {
        sqrtPriceX96: string;
        tickLower: number;
        tickUpper: number;
        amount0Desired: string;
        amount1Desired: string;
        amount0Min: string;
        amount1Min: string;
    };

    // Additional metadata for display
    marketCapUSD: number;
    pricePerTokenUSD: string;
    pricePerTokenETH: string;
}

/**
 * Calculate market cap in USD
 */
export function calculateMarketCapUSD(
    pricePerTokenInETH: number,
    ethUsdPrice: number
): number {
    return pricePerTokenInETH * TOTAL_SUPPLY * ethUsdPrice;
}

/**
 * Convert price to sqrtPriceX96
 * 
 * Formula: sqrtPriceX96 = sqrt(price) * 2^96
 * 
 * @param price - Price of token1 in terms of token0 (e.g., WETH per token)
 * @returns sqrtPriceX96 as a bigint
 */
export function priceToSqrtPriceX96(price: number): bigint {
    // Convert price to sqrt(price)
    const sqrtPrice = Math.sqrt(price);

    // Multiply by 2^96 and convert to bigint
    const sqrtPriceScaled = sqrtPrice * Number(Q96);

    // Handle very small numbers by using string conversion
    const sqrtPriceX96 = BigInt(Math.floor(sqrtPriceScaled));

    // Validate range
    if (sqrtPriceX96 < MIN_SQRT_RATIO || sqrtPriceX96 > MAX_SQRT_RATIO) {
        throw new Error(`sqrtPriceX96 out of valid range: ${sqrtPriceX96}`);
    }

    return sqrtPriceX96;
}

/**
 * Convert sqrtPriceX96 to nearest valid tick
 * 
 * Formula: tick = floor(log(sqrtPriceX96 / 2^96) / log(sqrt(1.0001)))
 * 
 * @param sqrtPriceX96 - Square root price in Q64.96 format
 * @returns Nearest valid tick (rounded to tick spacing)
 */
export function sqrtPriceX96ToTick(sqrtPriceX96: bigint): number {
    // Convert back to decimal sqrt price
    const sqrtPrice = Number(sqrtPriceX96) / Number(Q96);

    // Calculate tick: log(sqrtPrice) / log(sqrt(1.0001))
    // Simplified: log(sqrtPrice) / (0.5 * log(1.0001))
    const tick = Math.log(sqrtPrice) / (0.5 * Math.log(1.0001));

    // Round to nearest valid tick (must be multiple of TICK_SPACING)
    const roundedTick = Math.round(tick / TICK_SPACING) * TICK_SPACING;

    // Validate range
    if (roundedTick < MIN_TICK || roundedTick > MAX_TICK) {
        throw new Error(`Tick out of valid range: ${roundedTick}`);
    }

    return roundedTick;
}

/**
 * Main function: Compute all launch parameters for BOTH token orderings
 * 
 * @param startingPricePerTokenETH - Price per token in ETH (e.g., 0.0000000009315)
 * @param ethUsdPrice - Current ETH price in USD (for market cap calculation)
 * @returns Complete launch parameters for both orderings
 */
export function computeLaunchParams(
    startingPricePerTokenETH: number,
    ethUsdPrice: number = 2500
): LaunchParams {
    // Validate input
    if (startingPricePerTokenETH <= 0) {
        throw new Error('Starting price must be greater than 0');
    }

    // Total supply in wei (18 decimals)
    const totalSupplyWei = BigInt(TOTAL_SUPPLY) * BigInt(10) ** BigInt(18);

    // ============================================
    // SCENARIO 1: New Token is token0, WETH is token1
    // Price = WETH per token (as given)
    // ============================================

    const priceToken0 = startingPricePerTokenETH;
    const sqrtPriceX96Token0 = priceToSqrtPriceX96(priceToken0);
    const centerTickToken0 = sqrtPriceX96ToTick(sqrtPriceX96Token0);

    // For token0 scenario: range is BELOW current price (negative ticks)
    // Matches HalfETHPoolManager: tickLower = -214000, tickUpper = 0
    const tickUpperToken0 = 0; // Upper bound at tick 0
    const tickLowerToken0 = Math.floor((centerTickToken0 - 107 * TICK_SPACING) / TICK_SPACING) * TICK_SPACING;

    // Amount0 = all tokens, Amount1 = 0 (concentrated below current price)
    const amount0DesiredToken0 = totalSupplyWei;
    const amount1DesiredToken0 = BigInt(0);
    const amount0MinToken0 = (amount0DesiredToken0 * BigInt(99641)) / BigInt(100000); // ~99.64% like HalfETHPoolManager
    const amount1MinToken0 = BigInt(0);

    // ============================================
    // SCENARIO 2: WETH is token0, New Token is token1
    // Price = token per WETH (INVERTED!)
    // ============================================

    const priceToken1 = 1 / startingPricePerTokenETH; // Invert the price!
    const sqrtPriceX96Token1 = priceToSqrtPriceX96(priceToken1);
    const centerTickToken1 = sqrtPriceX96ToTick(sqrtPriceX96Token1);

    // For token1 scenario: range is ABOVE current price (positive ticks)
    // Matches HalfETHPoolManager: tickLower = 0, tickUpper = 213600
    const tickLowerToken1 = 0; // Lower bound at tick 0
    const tickUpperToken1 = Math.floor((centerTickToken1 + 107 * TICK_SPACING) / TICK_SPACING) * TICK_SPACING;

    // Amount0 = 0, Amount1 = all tokens (concentrated above current price)
    const amount0DesiredToken1 = BigInt(0);
    const amount1DesiredToken1 = totalSupplyWei;
    const amount0MinToken1 = BigInt(0);
    const amount1MinToken1 = amount1DesiredToken1; // 100% like HalfETHPoolManager

    // Calculate market cap
    const marketCapUSD = calculateMarketCapUSD(startingPricePerTokenETH, ethUsdPrice);

    return {
        paramsToken0: {
            sqrtPriceX96: sqrtPriceX96Token0.toString(),
            tickLower: tickLowerToken0,
            tickUpper: tickUpperToken0,
            amount0Desired: amount0DesiredToken0.toString(),
            amount1Desired: amount1DesiredToken0.toString(),
            amount0Min: amount0MinToken0.toString(),
            amount1Min: amount1MinToken0.toString(),
        },
        paramsToken1: {
            sqrtPriceX96: sqrtPriceX96Token1.toString(),
            tickLower: tickLowerToken1,
            tickUpper: tickUpperToken1,
            amount0Desired: amount0DesiredToken1.toString(),
            amount1Desired: amount1DesiredToken1.toString(),
            amount0Min: amount0MinToken1.toString(),
            amount1Min: amount1MinToken1.toString(),
        },
        marketCapUSD,
        pricePerTokenUSD: (startingPricePerTokenETH * ethUsdPrice).toFixed(10),
        pricePerTokenETH: startingPricePerTokenETH.toExponential(10),
    };
}

/**
 * Format token amount for display
 * 
 * @param amountWei - Amount in wei (18 decimals)
 * @param decimals - Token decimals (default: 18)
 * @returns Formatted string
 */
export function formatTokenAmount(amountWei: string, decimals: number = 18): string {
    const amount = BigInt(amountWei);
    const divisor = BigInt(10) ** BigInt(decimals);
    const whole = amount / divisor;
    const fraction = amount % divisor;

    // Format with commas
    const wholeStr = whole.toLocaleString();

    // Show first 6 decimal places
    const fractionStr = fraction.toString().padStart(decimals, '0').slice(0, 6);

    return `${wholeStr}.${fractionStr}`;
}
